import * as Sentry from '@sentry/react';
import ReclamationService from '../services/reclamation.service'
import { addNotification } from './notification.actions'
import {
  LOAD_RECLAMATIONS,
  LOAD_RECLAMATIONS_SUCCESS,
  RECLAMATION_ERROR,
  LOAD_RECLAMATION,
  LOAD_RECLAMATION_SUCCESS,
  LOAD_CATEGORIES,
  LOAD_STATUSES,
  LOAD_CATEGORIES_SUCCESS,
  LOAD_STATUSES_SUCCESS,
  LOAD_RECLAMATION_FILES
} from './types'
import FilesService from '../services/files.service'
import omit from 'lodash/omit'
import { saveAs } from 'file-saver'
import { removeHtml } from '../common/utils'

export function submitReclamation(data, history, sessionId) {
  return async dispatch => {
    try {
      // Initial breadcrumb for submission start
      Sentry.addBreadcrumb({
        category: 'reclamation',
        message: 'Reclamation submission started',
        level: 'info',
        data: {
          session_id: sessionId,
          files_count: data.files ? data.files.length : 0
        }
      });

      // Set session tag for this action
      Sentry.setTag('session_id', sessionId);
      Sentry.setTag('files_count', data.files ? data.files.length : 0);

      const reclamation = { ...omit(data, ['files']) }

      // Breadcrumb before creating reclamation
      Sentry.addBreadcrumb({
        category: 'reclamation',
        message: 'Submitting reclamation data to backend',
        level: 'info',
        data: {
          session_id: sessionId,
          residence: reclamation.rezidence,
          category: reclamation.kategorie
        }
      });

      const response = await ReclamationService.submit(reclamation)

      // Breadcrumb after successful reclamation creation
      Sentry.addBreadcrumb({
        category: 'reclamation',
        message: 'Reclamation created successfully',
        level: 'info',
        data: {
          session_id: sessionId,
          reclamation_id: response.data.id || 'unknown',
          folder: response.data.folder,
          response_data: JSON.stringify(response.data),
          response_status: response.status,
        }
      });

      // Set reclamation_id tag
      Sentry.setTag('reclamation_id', response.data.id || 'unknown');

      // Detailed file logging before upload
      const fileDetails = data.files ? data.files.map((file, index) => ({
        index,
        name: file.name || file.fileName || 'unknown',
        type: file.type || file.fileType || 'unknown',
        size: file.size || file.fileSize || 0,
        lastModified: file.lastModified || 'unknown'
      })) : [];

      // Breadcrumb before file upload
      Sentry.addBreadcrumb({
        category: 'file_upload',
        message: 'Starting file upload',
        level: 'info',
        data: {
          session_id: sessionId,
          reclamation_id: response.data.id || 'unknown',
          folder: response.data.folder,
          files_count: data.files ? data.files.length : 0,
          files: fileDetails
        }
      });

      // Only upload files if there are any
      if (data.files && data.files.length > 0) {
        await FilesService.createFolderAndUpload(data.files, response.data.folder)
      }

      // Breadcrumb after successful file upload
      Sentry.addBreadcrumb({
        category: 'file_upload',
        message: 'File upload completed successfully',
        level: 'info',
        data: {
          session_id: sessionId,
          reclamation_id: response.data.id || 'unknown',
          folder: response.data.folder,
          files_count: data.files ? data.files.length : 0
        }
      });

      // Log successful completion
      Sentry.captureMessage('Reclamation submitted successfully', {
        level: 'info',
        tags: {
          session_id: sessionId,
          reclamation_id: response.data.id || 'unknown',
          residence_id: reclamation.rezidence,
          files_count: data.files ? data.files.length : 0
        },
        extra: {
          folder: response.data.folder,
          submission_time: new Date().toISOString()
        }
      });

      history.push({
        pathname: '/submitted',
        state: { submitted: true },
      });
    } catch (e) {
      // Determine error type based on where it occurred
      let errorType = 'unknown';
      let errorContext = {};

      if (e.message && e.message.includes('upload')) {
        errorType = 'file_upload';
      } else if (e.message && e.message.includes('reclamation')) {
        errorType = 'reclamation_create';
      } else if (e.response) {
        // Network/API error
        errorType = 'network';
        errorContext = {
          status: e.response.status,
          statusText: e.response.statusText,
          url: e.response.config?.url
        };
      } else if (e.code === 'NETWORK_ERROR') {
        errorType = 'network';
      } else if (e.code === 'TIMEOUT') {
        errorType = 'timeout';
      }

      // Enhanced contextual information
      Sentry.setContext('reclamation_data', {
        session_id: sessionId,
        reclamation: omit(data, ['files']),
        files_count: data.files ? data.files.length : 0,
        files: data.files ? data.files.map(file => ({
          name: file.name || file.fileName || 'unknown',
          type: file.type || file.fileType || 'unknown',
          size: file.size || file.fileSize || 0,
          lastModified: file.lastModified || 'unknown'
        })) : [],
        error_type: errorType,
        error_context: errorContext,
      });

      // Add error breadcrumb
      Sentry.addBreadcrumb({
        category: 'error',
        message: `Reclamation submission failed: ${errorType}`,
        level: 'error',
        data: {
          session_id: sessionId,
          error_type: errorType,
          error_message: e.message,
          ...errorContext
        }
      });

      // Capture exception with enhanced tags
      Sentry.captureException(e, {
        tags: {
          action: 'submitReclamation',
          error_type: errorType,
          session_id: sessionId
        },
      });

      dispatch({
        type: RECLAMATION_ERROR,
        payload: e
      })
      dispatch(addNotification('Reklamaci se nepodařilo ulozit.', false))
    }
  }
}

export function editReclamation(data) {
  return async dispatch => {
    try {
      dispatch({ type: LOAD_RECLAMATIONS })

      await ReclamationService.edit(data)
      const request = await ReclamationService.list()

      dispatch({ type: LOAD_RECLAMATIONS_SUCCESS, payload: request })

      dispatch(addNotification('Reklamace upravena!', true))
    } catch (e) {
      dispatch({
        type: RECLAMATION_ERROR,
        payload: e
      })
      dispatch(addNotification('Reklamaci se nepodařilo upravit.', false))
    }
  }
}


export function confirm(data) {
  return async dispatch => {
    try {
      await ReclamationService.confirm(data)
    } catch (e) {
      dispatch({
        type: RECLAMATION_ERROR,
        payload: e
      })
    }
  }
}

export function loadReclamations() {
  return async dispatch => {
    try {
      dispatch({ type: LOAD_RECLAMATIONS })

      const request = await ReclamationService.list()

      dispatch({
        type: LOAD_RECLAMATIONS_SUCCESS,
        payload: request
      })
    } catch (e) {
      dispatch({
        type: RECLAMATION_ERROR,
        payload: e
      })

      dispatch(addNotification('Reklamaci se nepodařilo načíst.', false))
    }
  }
}

export function loadReclamation(id) {
  return async dispatch => {
    try {
      dispatch({ type: LOAD_RECLAMATION })

      const request = await ReclamationService.getByID(id)

      dispatch({
        type: LOAD_RECLAMATION_SUCCESS,
        payload: request
      })

      const filesRequest = await FilesService.getReclamationFiles(
        'reklamace/' + request.data.reclamation.folder
      )

      dispatch({
        type: LOAD_RECLAMATION_FILES,
        payload: filesRequest
      })
    } catch (e) {
      dispatch({
        type: RECLAMATION_ERROR,
        payload: e
      })

      dispatch(addNotification('Reklamaci se nepodařilo načíst.', false))
    }
  }
}

export function contactCompany(id) {
  return async dispatch => {
    try {
      await ReclamationService.contactCompany(id)

      dispatch(addNotification('Email odeslan!', true))
      dispatch({ type: LOAD_RECLAMATIONS })

      const request = await ReclamationService.list()

      dispatch({
        type: LOAD_RECLAMATIONS_SUCCESS,
        payload: request
      })
    } catch (e) {
      dispatch({
        type: RECLAMATION_ERROR,
        payload: e
      })

      dispatch(addNotification('Email se nepodařilo odeslat.', false))
    }
  }
}

export function loadCategories() {
  return async dispatch => {
    try {
      dispatch({ type: LOAD_CATEGORIES })

      const categories = await ReclamationService.getCategories()

      dispatch({
        type: LOAD_CATEGORIES_SUCCESS,
        payload: categories
      })
    } catch (e) {
      dispatch({
        type: RECLAMATION_ERROR,
        payload: e
      })

      dispatch(addNotification('Kategorie se nepodařilo načíst.', false))
    }
  }
}

export function loadStatuses() {
  return async dispatch => {
    try {
      dispatch({ type: LOAD_STATUSES })

      const statuses = await ReclamationService.getStatuses()

      dispatch({
        type: LOAD_STATUSES_SUCCESS,
        payload: statuses
      })
    } catch (e) {
      dispatch({
        type: RECLAMATION_ERROR,
        payload: e
      })

      dispatch(addNotification('Kategorie se nepodařilo načíst.', false))
    }
  }
}

export function exportData(ids) {
  return async dispatch => {
    try {
      const request = await ReclamationService.getByIDs(ids)
      const date = new Date()
      const fullDate =
        date.getDate() + '_' + date.getMonth() + '_' + date.getFullYear()
      let csvContent =
        '\ufeff' +
        'Interní evidenční číslo;Bytový dům;Stav reklamace;Popis závady\r\n'

      request.data.reclamations.forEach(function (r) {
        csvContent += `${r.id};${r.rezidence};${r.status};"${removeHtml(
          r.internalText
        )}"\r\n`
      })

      var blob = new Blob([csvContent], {
        type: 'text/plain;charset=utf-8'
      })

      saveAs(blob, `export_reklamace_${fullDate}.csv`)

      dispatch(addNotification('Export úspěšný.', true))
    } catch (e) {
      dispatch(addNotification('Export se nezdařil.', false))
    }
  }
}
